/**
 * Prescription data access, plus the doctor's autocomplete dictionaries.
 *
 * Replaces `renderPrescriptionsManagement`'s inline fetches, `savePrescription`,
 * `_prescAutocompleteTrigger` / `_prescAutocompleteSelect` and the duplicate
 * `openPrescriptionModal` / `closePrescriptionModal` pairs.
 */

import { useCallback, useMemo, useState } from 'react';
import { prescriptionApi, autocompleteApi, AUTOCOMPLETE_FIELDS } from '../api/endpoints';
import { useAsync } from './useAsync';

export function usePatientPrescriptions(patientId) {
  const state = useAsync(
    () => prescriptionApi.listForPatient(patientId),
    [patientId],
    { initialData: [], enabled: Boolean(patientId) },
  );
  return { ...state, prescriptions: state.data || [] };
}

export function useDoctorPrescriptions(doctorId) {
  const state = useAsync(
    () => prescriptionApi.listForDoctor(doctorId),
    [doctorId],
    { initialData: [], enabled: Boolean(doctorId) },
  );
  return { ...state, prescriptions: state.data || [] };
}

/**
 * The prescription attached to a booking, if any.
 * A 404 means "not written yet" and is a normal state, not an error.
 */
export function useBookingPrescription(bookingId) {
  const [notFound, setNotFound] = useState(false);

  const state = useAsync(
    async () => {
      setNotFound(false);
      try {
        return await prescriptionApi.getForBooking(bookingId);
      } catch (err) {
        if (err.status === 404) {
          setNotFound(true);
          return null;
        }
        throw err;
      }
    },
    [bookingId],
    { enabled: Boolean(bookingId) },
  );

  return { ...state, notFound, prescription: state.data };
}

/** Save/upsert a prescription and push its new terms into the dictionaries. */
export function usePrescriptionActions(doctorId) {
  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (payload) => {
      setSaving(true);
      try {
        const result = await prescriptionApi.save(payload);
        // Best-effort dictionary learning; a failure here must not fail the save.
        if (doctorId) {
          learnTerms(doctorId, payload).catch((err) =>
            console.warn('[Prescriptions] Could not update autocomplete:', err.message),
          );
        }
        return result?.prescription || result;
      } finally {
        setSaving(false);
      }
    },
    [doctorId],
  );

  const uploadResult = useCallback(
    (args) => prescriptionApi.uploadResult(args),
    [],
  );

  const commentResult = useCallback(
    (args) => prescriptionApi.commentResult(args),
    [],
  );

  return { save, saving, uploadResult, commentResult };
}

/**
 * Feed every term the doctor just typed back into their personal dictionary,
 * which is what makes autocomplete get better the more they prescribe.
 */
async function learnTerms(doctorId, prescription) {
  const terms = {
    diagnosis: prescription.diagnosis ? [prescription.diagnosis] : [],
    notes: prescription.notes ? [prescription.notes] : [],
    medicine: (prescription.medicines || []).map((m) => m?.name).filter(Boolean),
    labTest: (prescription.labTests || []).map((t) => t?.name).filter(Boolean),
    xray: (prescription.xrays || []).map((x) => x?.name).filter(Boolean),
  };

  await Promise.allSettled(
    Object.entries(terms)
      .filter(([, entries]) => entries.length > 0)
      .map(([field, entries]) => autocompleteApi.addEntries(doctorId, field, entries)),
  );
}

/**
 * The doctor's saved autocomplete dictionaries for all five fields.
 * `suggest(field, query)` does the prefix/substring match the legacy dropdown did.
 */
export function useAutocomplete(doctorId) {
  const state = useAsync(
    () => autocompleteApi.all(doctorId),
    [doctorId],
    {
      initialData: Object.fromEntries(AUTOCOMPLETE_FIELDS.map((f) => [f, []])),
      enabled: Boolean(doctorId),
    },
  );

  const dictionaries = useMemo(() => state.data || {}, [state.data]);

  const suggest = useCallback(
    (field, query, limit = 8) => {
      const entries = dictionaries[field] || [];
      const needle = String(query || '').trim().toLowerCase();
      if (!needle) return entries.slice(0, limit);

      // Prefix matches first, then anything containing the query.
      const starts = [];
      const contains = [];
      for (const entry of entries) {
        const lower = String(entry).toLowerCase();
        if (lower.startsWith(needle)) starts.push(entry);
        else if (lower.includes(needle)) contains.push(entry);
      }
      return [...starts, ...contains].slice(0, limit);
    },
    [dictionaries],
  );

  const fields = useMemo(() => AUTOCOMPLETE_FIELDS, []);

  return { ...state, dictionaries, suggest, fields };
}
